// src/components/AdminDashboard/sections/UserPurchasesSection.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  SectionCard,
  TableWrapper,
  StyledTable,
  StyledTableCell,
  NoDataMessage
} from '../AdminDashboard.styles';
import { media } from '../../../../utils/media';
import Pagination from '../../../Video/videoList/Pagination';
import { COLORS } from '../../../../BrandColors';

const UserPurchasesSection = ({
  purchases,
  loadingPurchases,
  purchaseError,
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchTerm,
  onSearchTermChange,
  filterCourt,
  onFilterCourtChange,
  filterDateRange,
  onFilterDateRangeChange,
  availableCourts,
  onClearFilters
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters =
    !!searchTerm || !!filterCourt || filterDateRange !== 'all';

  const formatDuration = (secondsValue) => {
    if (secondsValue == null || Number.isNaN(Number(secondsValue))) return 'N/A';

    const totalSeconds = Math.max(0, Number(secondsValue));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <SectionCard>
      <StyledAccordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography variant="h6">User Purchases</Typography>

            <Box display="flex" alignItems="center" gap={1}>
              {hasActiveFilters && (
                <Chip
                  label={`Filtered`}
                  color="primary"
                  size="small"
                />
              )}

              <Tooltip title="Toggle Filters">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilters((prev) => !prev);
                  }}
                  color={showFilters ? 'primary' : 'default'}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {showFilters && (
            <FiltersContainer>
              <SearchContainer>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by username, email, video title, court name, or purchase ID..."
                  value={searchTerm}
                  onChange={(e) => onSearchTermChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => onSearchTermChange('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </SearchContainer>

              <FilterControls>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Court</InputLabel>
                  <Select
                    value={filterCourt}
                    onChange={(e) => onFilterCourtChange(e.target.value)}
                    label="Court"
                  >
                    <MenuItem value="">All Courts</MenuItem>
                    {availableCourts.map((court) => (
                      <MenuItem key={court} value={court}>
                        {court}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={filterDateRange}
                    onChange={(e) => onFilterDateRangeChange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    label="Page Size"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>

                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onClearFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear Filters
                  </Button>
                )}
              </FilterControls>
            </FiltersContainer>
          )}

          <SummaryContainer>
            <Typography variant="body2" color="text.secondary">
              Showing {purchases.length} purchases (Page {currentPage} of {Math.max(totalPages, 1)})
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Total: {totalElements} purchases
            </Typography>
          </SummaryContainer>

          <EnhancedTableWrapper>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Purchase ID</StyledTableCell>
                  <StyledTableCell>Court Name</StyledTableCell>
                  <StyledTableCell>Username</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Video Title</StyledTableCell>
                  <StyledTableCell>Purchase Date</StyledTableCell>
                  <StyledTableCell>Duration</StyledTableCell>
                  <StyledTableCell>Price</StyledTableCell>
                  <StyledTableCell>Video Link</StyledTableCell>
                  <StyledTableCell>Discount Code</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loadingPurchases ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <LoadingContainer>
                        <CircularProgress />
                      </LoadingContainer>
                    </TableCell>
                  </TableRow>
                ) : purchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <EnhancedNoDataMessage>
                        No purchases found.
                      </EnhancedNoDataMessage>
                    </TableCell>
                  </TableRow>
                ) : (
                  purchases.map((purchase) => (
                    <TableRow
                      key={`purchase-${purchase.id ?? purchase.purchaseId ?? 'unknown'}-${purchase.purchaseDate ?? ''}`}
                    >
                      <TableCell>{purchase.id ?? purchase.purchaseId ?? 'N/A'}</TableCell>
                      <TableCell>{purchase.video?.courtName || 'N/A'}</TableCell>
                      <TableCell>{purchase.user?.username || 'N/A'}</TableCell>
                      <TableCell>{purchase.user?.email || 'N/A'}</TableCell>
                      <TableCell>{purchase.video?.title || 'N/A'}</TableCell>
                      <TableCell>
                        {purchase.purchaseDate
                          ? new Date(purchase.purchaseDate).toLocaleString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {formatDuration(purchase.videoDuration ?? purchase.video?.duration)}
                      </TableCell>
                      <TableCell>
                        {purchase.purchase?.price != null
                          ? `$${Number(purchase.purchase.price).toFixed(2)}`
                          : purchase.price != null
                            ? `$${Number(purchase.price).toFixed(2)}`
                            : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {purchase.video?.preSignedS3Url ? (
                          <a
                            href={purchase.video.preSignedS3Url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Video
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {purchase.discountCode || purchase.purchase?.discountCode?.code || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StyledTable>

            {purchaseError && <Typography color="error">{purchaseError}</Typography>}
          </EnhancedTableWrapper>

          {!loadingPurchases && totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </PaginationContainer>
          )}
        </AccordionDetails>
      </StyledAccordion>
    </SectionCard>
  );
};

UserPurchasesSection.propTypes = {
  purchases: PropTypes.array.isRequired,
  loadingPurchases: PropTypes.bool.isRequired,
  purchaseError: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalElements: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchTermChange: PropTypes.func.isRequired,
  filterCourt: PropTypes.string.isRequired,
  onFilterCourtChange: PropTypes.func.isRequired,
  filterDateRange: PropTypes.string.isRequired,
  onFilterDateRangeChange: PropTypes.func.isRequired,
  availableCourts: PropTypes.array.isRequired,
  onClearFilters: PropTypes.func.isRequired
};

export default UserPurchasesSection;

/* ─────────────────────────────────────────────────────────────
   STYLED COMPONENTS
────────────────────────────────────────────────────────────── */
const FiltersContainer = styled.div`
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, ${COLORS.primaryBackground} 0%, #f8f9fa 100%);
  border-radius: 12px;
  border: 2px solid ${COLORS.accentTeal}20;
  box-shadow: 0 4px 12px rgba(0, 166, 147, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 166, 147, 0.15);
    border-color: ${COLORS.accentTeal}40;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const FilterControls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  .MuiButton-outlined {
    border-color: ${COLORS.secondaryButton};
    color: ${COLORS.secondaryButton};
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
      background-color: ${COLORS.secondaryButton};
      color: white;
      border-color: ${COLORS.secondaryButton};
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(189, 32, 38, 0.3);
    }
  }

  ${media.mobile`
    flex-direction: column;
    align-items: stretch;
  `}
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(90deg, ${COLORS.accentTeal}10 0%, ${COLORS.accentOrange}10 100%);
  border-radius: 8px;
  border-left: 4px solid ${COLORS.accentTeal};

  ${media.mobile`
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  `}
`;

const PaginationContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
  padding: 16px;
  background-color: ${COLORS.primaryBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EnhancedTableWrapper = styled(TableWrapper)`
  .MuiTable-root {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    .MuiTableHead-root {
      background: linear-gradient(135deg, ${COLORS.accentTeal} 0%, ${COLORS.accentBlueGray} 100%);

      .MuiTableCell-head {
        color: white;
        font-weight: 700;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-bottom: none;
      }
    }

    .MuiTableBody-root {
      .MuiTableRow-root {
        transition: background-color 0.2s ease;

        &:hover {
          background-color: ${COLORS.accentTeal}08;
        }

        &:nth-of-type(even) {
          background-color: ${COLORS.primaryBackground};
        }

        .MuiTableCell-body {
          border-bottom: 1px solid ${COLORS.accentBlueGray}20;
          color: ${COLORS.accentBlueGray};
          font-weight: 500;

          a {
            color: ${COLORS.accentTeal};
            text-decoration: none;
            font-weight: 600;

            &:hover {
              color: ${COLORS.accentOrange};
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
`;

const StyledAccordion = styled(Accordion)`
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid ${COLORS.accentTeal}20;

  .MuiAccordionSummary-root {
    background: linear-gradient(135deg, ${COLORS.primaryBackground} 0%, ${COLORS.accentTeal}05 100%);
    border-bottom: 2px solid ${COLORS.accentTeal}20;
  }

  .MuiAccordionDetails-root {
    background-color: ${COLORS.primaryBackground};
    padding: 24px;
  }
`;

const EnhancedNoDataMessage = styled(NoDataMessage)`
  color: ${COLORS.accentBlueGray};
  font-size: 1.1rem;
  font-weight: 500;
  padding: 40px 20px;
  background: linear-gradient(135deg, ${COLORS.accentTeal}05 0%, ${COLORS.accentOrange}05 100%);
  border-radius: 8px;
  border: 2px dashed ${COLORS.accentTeal}40;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;

  .MuiCircularProgress-root {
    color: ${COLORS.accentTeal};
  }
`;